import apiClient from './client';

export interface SeriesCosts {
  series_id: string;
  series_title: string;
  total_cost_usd: number;
  total_tokens: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  operations_count: number;
  chapters_count: number;
  models_used: Array<{
    model: string;
    provider: string;
    operations: number;
    cost: number;
  }>;
  breakdown_by_operation: Array<{
    operation_type: string;
    count: number;
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    total_cost_usd: number;
  }>;
  breakdown_by_chapter: Array<{
    chapter_id: string;
    chapter_no: number;
    operations_count: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_tokens: number;
    total_cost_usd: number;
  }>;
}

export interface CostsSummary {
  overall: {
    total_series: number;
    total_chapters: number;
    total_operations: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_tokens: number;
    total_cost_usd: number;
  };
  by_series: Array<{
    series_id: string;
    series_title: string;
    total_cost_usd: number;
    total_tokens: number;
    operations_count: number;
    chapters_count: number;
    models_used: Array<{
      model: string;
      provider: string;
      operations: number;
      cost: number;
    }>;
  }>;
  by_provider: Array<{
    provider: string;
    operations_count: number;
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    total_cost_usd: number;
  }>;
  by_model: Array<{
    model_name: string;
    provider: string;
    operations_count: number;
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    total_cost_usd: number;
  }>;
}

export interface UserUsage {
  user_id: string;
  email: string;
  username: string | null;
  role: string;
  chapters: number;
  cost_usd: number;
  tokens: number;
}

export const costsApi = {
  getBySeries: async (seriesId: string): Promise<SeriesCosts> => {
    try {
      const response = await apiClient.get<SeriesCosts>(`/series/${seriesId}/costs`);
      return response.data;
    } catch (error: unknown) {
      // If 404, return empty stats instead of throwing error
      const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
      if (status === 404) {
        return {
          series_id: seriesId,
          series_title: '',
          total_cost_usd: 0,
          total_tokens: 0,
          total_prompt_tokens: 0,
          total_completion_tokens: 0,
          operations_count: 0,
          chapters_count: 0,
          models_used: [],
          breakdown_by_operation: [],
          breakdown_by_chapter: []
        };
      }
      throw error;
    }
  },

  getSummary: async (): Promise<CostsSummary> => {
    const response = await apiClient.get<CostsSummary>('/costs/summary');
    return response.data;
  },

  getTrends: async (days: number = 30): Promise<{ data: Array<{ date: string; cost: number; operations: number; model_name: string; operation_type: string }> }> => {
    const response = await apiClient.get<{ data: Array<{ date: string; cost: number; operations: number; model_name: string; operation_type: string }> }>(`/costs/trends?days=${days}`);
    return response.data;
  },

  getByUser: async (): Promise<{ data: UserUsage[] }> => {
    const response = await apiClient.get<{ data: UserUsage[] }>('/costs/by-user');
    return response.data;
  },
};
